// Jobs page: one trigger plus one instruction, which names the Skill.
const jobs = {
  title: "Jobs",
  description: "Skills that run on their own, on a schedule or when a webhook is called.",
  newJob: "New job",
  empty: {
    title: "No jobs yet",
    description: "A job runs a Skill on its own: every weekday at 9:00, or whenever a system calls its webhook.",
    adminHint: "Create one here, or ask the AI in the chat.",
  },
  loadFailed: "The jobs could not be loaded.",
  noWorkspace: "Select a workspace to see its jobs.",
  tabs: {
    jobs: "Jobs",
    runs: "Runs",
  },
  // Job proposed from a Skill
  proposal: {
    title: "Building the job from the Skill",
    stages: {
      loading: "Loading the form",
      filling: "Filling in the job from the Skill",
      finishing: "Making the final adjustments",
    },
    reason: "AI proposal: {{reason}} Review before saving.",
    ready: "the proposal was built from the Skill.",
    failed: "The proposal could not be built. The Skill is already cited in the instruction; fill in the rest by hand.",
  },
  // References in the instruction with @
  mentions: {
    skill: "Skill",
    workspace: "Workspace",
    empty: "Nothing found.",
    loading: "Loading…",
    unknown: "Not found in this workspace: the job will not be saved with this reference.",
  },
  // Run history across every job
  runs: {
    searchPlaceholder: "Search the summary or by job name",
    filter: {
      all: "All",
      running: "Running",
      completed: "Completed",
      failed: "Failed",
      job: "Job",
    },
    period: {
      "24h": "Last 24 hours",
      "7d": "Last 7 days",
      "30d": "Last 30 days",
      all: "All time",
    },
    columns: {
      job: "Job",
      status: "Status",
      trigger: "Trigger",
      started: "Started",
      duration: "Duration",
      summary: "Summary",
      writes: "Changes",
      pending: "Pending",
    },
    status: {
      running: "Running",
      waiting: "Waiting",
      completed: "Completed",
      failed: "Failed",
      cancelled: "Cancelled",
    },
    trigger: {
      schedule: "Schedule",
      webhook: "Webhook",
    },
    empty: {
      title: "No runs yet",
      description: "When a published job fires, each run shows here, with the summary of what the agent did.",
    },
    noResults: "No runs match these filters.",
    loadFailed: "The runs could not be loaded.",
    loadMore: "Load more",
    loadingRun: "Loading the run…",
    runLoadFailed: "This run could not be loaded.",
    openJob: "Open the job",
    actionsTitle: "Actions",
    actionFailed: "failed",
    usage: "{{calls}} model calls · {{tokens}} tokens",
  },
  filter: {
    searchPlaceholder: "Search by name, instruction or Skill",
    clearSearch: "Clear search",
    all: "All",
    published: "Published",
    draft: "Drafts",
    trigger: "Trigger",
    schedule: "On a schedule",
    webhook: "Webhook",
    clear: "Clear filters",
    noResults: "No jobs match these filters.",
  },
  columns: {
    name: "Name",
    instruction: "Instruction",
    trigger: "Trigger",
    status: "Status",
    lastRun: "Last run",
    actions: "Actions",
  },
  status: {
    draft: "Draft",
    published: "Published",
  },
  lastRun: {
    never: "Never ran",
  },
  actions: {
    test: "Test",
    publish: "Publish",
    edit: "Edit",
    details: "Details",
    delete: "Delete",
  },
  delete: {
    confirmTitle: "Delete {{name}}?",
    confirmDescription: "The job stops running and its run history is deleted with it. This cannot be undone.",
    confirmDescriptionWebhook:
      "The job stops running, its run history is deleted with it and the webhook address stops accepting calls. This cannot be undone.",
    confirm: "Delete",
    deleting: "Deleting…",
    cancel: "Cancel",
    success: "{{name}} was deleted",
  },
  form: {
    createTitle: "New job",
    editTitle: "Edit job",
    description: "Say what the agent must do — naming the Skill it must follow — and when it runs. Test runs it once, without publishing; Publish saves it and the job starts running.",
    name: "Name",
    namePlaceholder: "Daily evidences from meetings",
    descriptionLabel: "Description",
    descriptionPlaceholder: "What this job does (optional)",
    instruction: "Instruction",
    instructionPlaceholder: "Follow @skill:meeting-insights-to-evidence and register one Evidence for each relevant meeting insight of today.",
    instructionHelp: "What the agent must do on each run. Type @ to cite a Skill or a workspace context field; the cited Skill is the one the agent follows, and the field goes along as material.",
    trigger: "Trigger",
    triggerSchedule: "On a schedule",
    triggerWebhook: "When a webhook is called",
    webhookHelp: "The job runs when an external system calls its address. What it sends reaches the agent as supporting material.",
    frequency: "Frequency",
    presets: {
      everyMinutes: "Every few minutes",
      everyHours: "Every few hours",
      daily: "Every day",
      weekdays: "Weekdays",
      weekly: "Weekly",
      monthly: "Monthly",
      custom: "Advanced (cron)",
    },
    time: "Time",
    every: "Every",
    minutes: "{{count}} minutes",
    hours_one: "{{count}} hour",
    hours_other: "{{count}} hours",
    days: "Days of the week",
    monthDay: "Day of the month",
    lastDay: "Last day of the month",
    window: "Only within a time window",
    windowFrom: "From",
    windowTo: "Until",
    weekdaysOnly: "Only on weekdays",
    cron: "Cron expression",
    cronHelp: "Five fields: minute hour day-of-month month day-of-week. 0 or 7 = Sunday, L = last day of the month. Example: 0 9 * * 1-5",
    cronFields: {
      minute: "minute",
      hour: "hour",
      day: "day of the month",
      month: "month",
      weekday: "day of the week",
    },
    invalidCronField: "Invalid value in the {{field}} field",
    invalidWindow: "The end must be after the start",
    pickDays: "Choose at least one day",
    timezone: "Time zone",
    timezoneSearch: "Search time zone",
    timezoneEmpty: "No time zone found",
    nextRuns: "Next runs",
    noPreview: "Fix the schedule to see when it runs",
    advanced: "Limits",
    timeout: "Time limit per run (minutes)",
    timeoutHelp: "1 to 60. Default 15.",
    maxWrites: "Changes in external systems per run",
    maxWritesHelp: "0 to 200. Default 20.",
    cancel: "Cancel",
    test: "Test",
    publish: "Publish",
    publishing: "Publishing…",
    required: "Required",
    invalidTime: "Use HH:MM",
    invalidCron: "Use five fields: minute hour day-of-month month day-of-week",
  },
  saved: {
    created: "Job saved as a draft",
    updated: "Job updated",
    nextStep: "Test it, then publish to start running it.",
  },
  webhook: {
    url: "Webhook address",
    authType: "Authentication",
    basic: "User and password (Basic)",
    bearer: "Token (Bearer)",
    username: "User",
    password: "Password",
    token: "Token",
    tokenPlaceholder: "Leave empty for Prodgy to generate one",
    show: "Show",
    hide: "Hide",
    copy: "Copy",
    copied: "Copied",
    loading: "Loading the webhook…",
    handoffTitle: "Webhook ready",
    handoffDescription: "Configure these in the system that will call the webhook. The job only receives calls after it is published; you can see these again in Edit.",
    basicHint: "The caller sends the Authorization: Basic header, with the user and password below.",
    bearerHint: "The caller sends the Authorization: Bearer header, with the token below.",
    done: "Done",
  },
  test: {
    title: "Test: {{name}}",
    description: "Runs once, without publishing.",
    stages: {
      queued: "Queued",
      working: "Agent working",
      result: "Result",
      completed: "Completed",
      failed: "Failed",
    },
    showMore: "Show more",
    showLess: "Show less",
    pendingCount: "{{count}} pending",
    summary: "Summary",
    pending: "Left pending",
    actions: "{{count}} actions",
    writes: "{{count}} changes in external systems",
    noReport: "The agent returned no report.",
    close: "Close",
    publish: "Publish",
  },
  publish: {
    confirmTitle: "Publish {{name}}?",
    confirmDescription: "From now on the trigger fires on its own. You can still edit the draft and publish again later.",
    confirm: "Publish",
    publishing: "Publishing…",
    cancel: "Cancel",
    success: "Job published",
  },
  settings: {
    jobNotice: "This is a job: one trigger and one agent step, edited on the Jobs page. Adding steps here turns it into a flow, and it moves to the Flows page.",
  },
};

export default jobs;
